import { NavLink } from 'react-router-dom';
import { IconGrid } from '@/components/ui/Icon';
import { useCustomMenuItems } from '@/shared/hooks/useCustomMenuItems';
import { useCustomObjects } from '@/shared/hooks/useCustomObjects';

// Appended, additive top-level nav tabs for (a) per-company custom menu items
// and (b) custom objects (each links to its record list). Renders nothing when
// there are none, so the static nav is untouched.
// The tabs it shows; MainNav's Priority+ re-measures when their number changes.
export function useCustomNavEntries() {
  const items = useCustomMenuItems().filter((i) => i.is_active && !i.parent_section);
  const objects = useCustomObjects();
  return { items, objects };
}

// Each tab carries data-custom-navtab: MainNav's Priority+ reserves their
// live width in the row (they grow to 44px on touch, D7).
export function CustomNavTabs({ compact }: { compact: boolean }) {
  const { items, objects } = useCustomNavEntries();
  if (items.length === 0 && objects.length === 0) return null;

  const cls = ({ isActive }: { isActive: boolean }) => `${compact ? 'w-6 h-6 justify-center' : 'px-2 h-6 gap-1.5'} rounded-full text-[0.75rem] font-medium tracking-tight inline-flex items-center transition-colors duration-150 border focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
    isActive
      ? 'bg-primary text-white font-semibold border-primary shadow-sm focus-visible:ring-white'
      : 'text-slate-800 hover:text-primary-text border-transparent hover:bg-slate-50 focus-visible:ring-primary'
  }`;
  const content = (label: string, isActive = false) => (<><IconGrid size={13} />{!compact && <span className={isActive ? 'inline' : 'hidden lg:inline'}>{label}</span>}</>);

  return (
    <>
      {items.map((i) => {
        const external = /^https?:\/\//i.test(i.target_ref);
        return external ? (
          <a key={i.id} href={i.target_ref} target="_blank" rel="noreferrer" data-custom-navtab title={i.label} aria-label={i.label} className={cls({ isActive: false })}>{content(i.label)}</a>
        ) : (
          <NavLink key={i.id} to={i.target_ref} data-custom-navtab title={i.label} aria-label={i.label} className={cls}>{({ isActive }) => content(i.label, isActive)}</NavLink>
        );
      })}
      {objects.map((o) => (
        <NavLink key={o.id} to={`/custom/${o.key}`} data-custom-navtab title={o.label_plural} aria-label={o.label_plural} className={cls}>{({ isActive }) => content(o.label_plural, isActive)}</NavLink>
      ))}
    </>
  );
}
